import { SOPTemplate } from "@features/wx/dispatcher/types";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Clock, DollarSign, Search, Users, Wrench } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

interface SOPTemplatesPanelProps {
  templates: SOPTemplate[];
  selectedSOP: SOPTemplate | null;
  onSelectSOP: (sop: SOPTemplate) => void;
  totalCount?: number;
  hasMore?: boolean;
  isLoading?: boolean; // Add loading state
}

const getPriorityColor = (tag: string) => {
  switch (tag) {
    case "Hot":
    case "Critical":
    case "critical":
      return "bg-red-500 text-white";
    case "High":
    case "high":
      return "bg-orange-500 text-white";
    case "Medium":
    case "medium":
      return "bg-blue-500 text-white";
    case "Low":
    case "low":
      return "bg-green-500 text-white";
    default:
      return "bg-gray-500 text-white";
  }
};

const getAssociationIcon = (association: string) => {
  switch (association.toLowerCase()) {
    case "meter":
      return <Wrench className="h-4 w-4" />;
    case "consumer":
      return <Users className="h-4 w-4" />;
    case "asset":
      return <Clock className="h-4 w-4" />;
    default:
      return <Search className="h-4 w-4" />;
  }
};

export const SOPTemplatesPanel: React.FC<SOPTemplatesPanelProps> = ({
  templates,
  selectedSOP,
  onSelectSOP,
  totalCount = 0,
  hasMore = false,
  isLoading = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current filter values from URL
  const currentSearch = searchParams.get("sopSearch") || "";
  const currentCategory = searchParams.get("sopCategory") || "all";

  // Local state for input values (for immediate UI feedback)
  const [searchInputValue, setSearchInputValue] = useState(currentSearch);
  const [categoryValue, setCategoryValue] = useState(currentCategory);

  // Update local state when URL changes
  useEffect(() => {
    setSearchInputValue(currentSearch);
    setCategoryValue(currentCategory);
  }, [currentSearch, currentCategory]);

  // Debounced search function
  const debouncedSearch = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchTerm: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const params = new URLSearchParams(searchParams);
        if (searchTerm.trim()) {
          params.set("sopSearch", searchTerm);
        } else {
          params.delete("sopSearch");
        }
        // Reset pagination when searching
        params.delete("sopPage");
        setSearchParams(params);
      }, 500);
    };
  }, [searchParams, setSearchParams]);

  // Handle category change
  const handleCategoryChange = useCallback(
    (value: string) => {
      setCategoryValue(value);
      const params = new URLSearchParams(searchParams);
      if (value === "all") {
        params.delete("sopCategory");
      } else {
        params.set("sopCategory", value);
      }
      // Reset pagination when changing category
      params.delete("sopPage");
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInputValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("sopSearch");
    params.delete("sopCategory");
    params.delete("sopPage");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  // Check if any filters are active
  const hasActiveFilters =
    currentSearch || (currentCategory && currentCategory !== "all");

  return (
    <div className="flex flex-col h-full max-h-full bg-white">
      {/* Fixed Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 truncate">
            SOP Templates
          </h2>
        </div>

        {/* Controls Row */}
        <div className="flex gap-3 mb-3">
          <Select value={categoryValue} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-36 text-sm border-gray-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-200">
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="0">Consumer</SelectItem>
              <SelectItem value="1">Meter</SelectItem>
              <SelectItem value="2">Asset</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search templates..."
              value={searchInputValue}
              onChange={handleSearchChange}
              className="pl-9 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Results info and clear filters */}
        <div className="flex items-center justify-between text-sm">
          <div className="text-gray-600">
            <>
              Showing {templates.length} templates
              {currentSearch && (
                <span className="text-blue-600 ml-1">
                  matching "{currentSearch}"
                </span>
              )}
              {totalCount > 0 && (
                <span className="text-gray-500 ml-1">
                  of {totalCount} total
                </span>
              )}
            </>
          </div>

          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="text-xs"
              disabled={isLoading}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-4 space-y-3">
          {templates.length === 0 ? (
            <div className="text-center py-12">
              <Search className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">
                No SOP templates found
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {hasActiveFilters
                  ? "Try adjusting your search or filters"
                  : "No templates available"}
              </p>
            </div>
          ) : (
            templates.map((template) => (
              <div
                key={template.id}
                onClick={() => {
                  onSelectSOP(template);
                }}
                className={`group p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                  selectedSOP?.id === template.id
                    ? "border-blue-500 bg-blue-50 shadow-md"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                {/* Header Row */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                    {template.soId}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm leading-5 line-clamp-2">
                          {template.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {template.shortName} • {template.version}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Metrics Row */}
                <div className="grid grid-cols-3 gap-4 mb-3 text-xs">
                  <div className="flex items-center gap-1 text-gray-600">
                    {getAssociationIcon(template.association)}
                    <span className="truncate">{template.association}</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-600">
                    <Clock className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">TAT: {template.tat}</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-600">
                    <DollarSign className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{template.estimatedCost}</span>
                  </div>
                </div>

                {/* Service Tags */}
                {template.utilityServices &&
                  template.utilityServices.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {template.utilityServices
                        .slice(0, 2)
                        .map((service, index) => (
                          <Badge
                            key={index}
                            className="bg-blue-100 text-blue-800 border-0 text-xs px-2 py-0.5"
                          >
                            {service}
                          </Badge>
                        ))}
                      {template.utilityServices.length > 2 && (
                        <Badge className="bg-gray-100 text-gray-700 border-0 text-xs px-2 py-0.5">
                          +{template.utilityServices.length - 2}
                        </Badge>
                      )}
                    </div>
                  )}

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Badge className="bg-gray-100 text-gray-700 border-0 text-xs px-2 py-0.5 break-words">
                    {template.sopType}
                  </Badge>
                  {template.sopSubtype && (
                    <Badge className="bg-gray-100 text-gray-700 border-0 text-xs px-2 py-0.5 break-words p-2">
                      {template.sopSubtype}
                    </Badge>
                  )}
                </div>

                {/* Description */}
                <p className="text-xs text-gray-600 line-clamp-2 leading-4 mb-3">
                  {template.description}
                </p>

                {/* Footer Stats */}
                <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    {template.usageCount > 0 && (
                      <span>Used {template.usageCount} times</span>
                    )}
                    {template.tasks && template.tasks.length > 0 && (
                      <span>{template.tasks.length} tasks</span>
                    )}
                    {template.materials && template.materials.length > 0 && (
                      <span>{template.materials.length} materials</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Showing {templates.length} templates</span>
          {hasMore && (
            <span className="ml-2 text-blue-600">
              • More available via API pagination
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
